import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from '@hooks/redux';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import SessionTimeoutWarning from '@components/auth/SessionTimeoutWarning';
import { OfflineIndicator } from '@components/common/OfflineIndicator';
import { AppLayout } from '@components/layout';
import LoginPage from '@pages/auth/LoginPage';
import DashboardPage from '@pages/DashboardPage';
import UnauthorizedPage from '@pages/UnauthorizedPage';
import DevicesPage from '@pages/DevicesPage';
import DeviceDetailsPage from '@pages/DeviceDetailsPage';
import SettingsPage from '@pages/SettingsPage';
import LabelManagementPage from '@pages/LabelManagementPage';
import NonTPMDevicesPage from '@pages/NonTPMDevicesPage';
import ClientUpdatesPage from '@pages/ClientUpdatesPage';
import DotNetAppUpdatesPage from '@pages/DotNetAppUpdatesPage';
import MaintenancePage from '@pages/MaintenancePage';
import DeploymentDetailPage from '@pages/DeploymentDetailPage';
import DPSEnrollmentPage from '@pages/DPSEnrollmentPage';
import DfoRegistryPage from '@pages/DfoRegistryPage';
import UserManagementPage from '@pages/UserManagementPage';
import AuditLogPage from '@pages/AuditLogPage';
import ExportPage from '@pages/ExportPage';
import authService from '@services/authService';
import { AnnouncementProvider } from '@contexts/AnnouncementContext';
import './styles/focus.css';
import { BUILD_VERSION } from './version';

const App: React.FC = () => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    // Initialize auth service
    authService.init();
    // Log build version
    console.log('Build Version:', BUILD_VERSION);
    (window as any).BUILD_VERSION = BUILD_VERSION;
  }, []);

  return (
    <Router>
        <AnnouncementProvider>
          <div>
            {/* Offline indicator */}
            <OfflineIndicator position="top" />

            {/* Session timeout warning modal */}
            <SessionTimeoutWarning />

        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/devices/:deviceId" element={<DeviceDetailsPage />} />
              <Route path="/labels" element={<LabelManagementPage />} />
              <Route path="/export" element={<ExportPage />} />
              <Route path="/dps-enrollment" element={<DPSEnrollmentPage />} />
              <Route path="/dfo-registry" element={<DfoRegistryPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<div>Profile Page (TODO)</div>} />

              {/* Admin only routes */}
              <Route element={<ProtectedRoute requireRole="admin" />}>
                <Route path="/non-tpm-devices" element={<NonTPMDevicesPage />} />
                <Route path="/client-updates" element={<ClientUpdatesPage />} />
                <Route path="/dotnet-app-updates" element={<DotNetAppUpdatesPage />} />
                <Route path="/maintenance" element={<MaintenancePage />} />
                <Route path="/deployments/:deploymentId" element={<DeploymentDetailPage />} />
                <Route path="/users" element={<UserManagementPage />} />
                <Route path="/audit-logs" element={<AuditLogPage />} />
                <Route path="/settings/system" element={<div>System Settings (Admin Only)</div>} />
              </Route>
            </Route>
          </Route>

          {/* Default redirect */}
          <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
        </Routes>
      </div>
        </AnnouncementProvider>
    </Router>
  );
};

export default App;
